import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AccountDisplayStatus, AccountSearchCriteria} from 'web-angular-client';
import {AccountStatusUtil} from '../../../sdk/interface/accountStatusUtil';
import {SearchService} from '../../../sdk/component/search/search.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {AccountSearchCriteriaImpl} from '../../../sdk/component/search/account-search-criteria';
import {ActivatedRoute} from '@angular/router';
import {SearchFilterBase} from '../../../sdk/component/search/search-filter-base';
import {Helper} from '../../../sdk/util/helper';
import {FilterPersistenceService} from '../../../sdk/component/search/filter-persistence.service';
import {DropdownItem} from '../../../sdk/interface/data/DropdownItem';
import {formatLocalYMD, toUtcIsoAtLocalEndOfDay, toUtcIsoAtLocalStartOfDay} from '../../../sdk/util/searchUtil';

@Component({
    selector: 'app-payment-search',
    templateUrl: './payment-search.component.html',
    styleUrls: ['./payment-search.component.scss'],
    standalone: false
})
export class PaymentSearchComponent extends SearchFilterBase implements OnInit, OnDestroy
{
    @Input() showBatchFilter: boolean = true;
    @Input() showEcommerceStoreFilter: boolean = false;
    @Input() ecommerceStoreOptions: DropdownItem[] = [];
    @Input() showCsvExport: boolean = false;
    @Input() csvPeriodOptions: DropdownItem[] = [];
    @Input() csvSelectedPeriod: string | null = null;
    @Input() csvDownloading: boolean = false;
    @Output('displayBatchNumber') displayBatchNumber: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() csvPeriodChange: EventEmitter<string> = new EventEmitter<string>();
    @Output() csvDownload: EventEmitter<void> = new EventEmitter<void>();
    form: UntypedFormGroup;
    protected _AMOUNT_UPPER_INPUT_ID: string = 'amountUpper';
    protected _AMOUNT_LOWER_INPUT_ID: string = 'amountLower';
    protected _DATE_TIME_CREATED_LOWER_INPUT_ID: string = 'dateTimeCreatedLower';
    protected _DATE_TIME_CREATED_UPPER_INPUT_ID: string = 'dateTimeCreatedUpper';
    protected _DISPLAY_STATUS_INPUT_ID: string = 'displayStatus';
    protected _ECOMMERCE_STORE_IDS_INPUT_ID: string = 'ecommerceStoreIds';
    protected _INDIVIDUAL_ACCOUNTS_INPUT_ID: string = 'individualAccounts';
    protected _ALL_ACCOUNTS_INPUT_ID: string = 'allAccounts';
    protected _DATE_TIME_PAID_LOWER_INPUT_ID: string = 'dateTimePaidLower';
    protected _DATE_TIME_PAID_UPPER_INPUT_ID: string = 'dateTimePaidUpper';
    protected DATE_CREATED_FILTER_ID: string = 'dateCreatedFilter';
    protected AMOUNT_FILTER_ID: string = 'amountFilter';
    protected ACCOUNT_STATUS_FILTER_ID: string = 'accountStatusFilter';
    protected DATE_PAID_FILTER_ID: string = 'datePaidFilter';
    protected ECOMMERCE_STORE_FILTER_ID: string = 'ecommerceStoreFilter';
    protected _REFERENCE_NUMBER_INPUT_ID: string = 'referenceNumber';
    protected _CUSTOMER_INPUT_ID: string = 'customer';
    protected _ACCOUNT_NUMBER_INPUT_ID: string = 'accountNumber';
    protected _BATCH_ACCOUNTS_RADIO_INPUT_ID: string = 'batchAccounts';
    protected _BATCH_ACCOUNTS_TEXT_INPUT_ID: string = 'batchNumber';
    private NAME_FILTER_ID: string = 'nameFilter';
    private REFERENCE_FILTER_ID: string = 'referenceNumberFilter';
    private ACCOUNT_NUMBER_FILTER_ID: string = 'accountNumberFilter';
    private BATCH_FILTER_ID: string = 'batchFilter';
    private accountSearchCriteria: AccountSearchCriteriaImpl = new AccountSearchCriteriaImpl();

    constructor(
        protected searchDataService: SearchService,
        protected filterPersistenceService: FilterPersistenceService,
        protected route: ActivatedRoute,
        protected accountStatusUtil: AccountStatusUtil,
        protected formBuilder: UntypedFormBuilder,
        protected ngxLoader: NgxUiLoaderService,
        protected activatedRoute: ActivatedRoute)
    {
        super(searchDataService, ngxLoader, filterPersistenceService, route);
    }

    get activeTab(): string
    {
        return this._activeTab;
    }

    set activeTab(value: string)
    {
        this._activeTab = value;
    }

    set status(val: string)
    {
        this.accountSearchCriteria.status = this.accountStatusUtil.getAccountStateFromDescription(<string> val);
    }

    get amountLower()
    {
        return this.form.get(this._AMOUNT_LOWER_INPUT_ID).value;
    }

    set amountLower(val)
    {
        this.accountSearchCriteria.amountLower = val;
    }

    set dateTimeCreatedLower(val: Date)
    {
        this.accountSearchCriteria.dateTimeCreatedLower = toUtcIsoAtLocalStartOfDay(val);
    }

    set dateTimeCreatedUpper(val: Date)
    {
        this.accountSearchCriteria.dateTimeCreatedUpper = toUtcIsoAtLocalEndOfDay(val);
    }

    set dateTimePaidLower(val: Date)
    {
        this.accountSearchCriteria.dateTimePaidLower = toUtcIsoAtLocalStartOfDay(val);
    }

    set dateTimePaidUpper(val: Date)
    {
        this.accountSearchCriteria.dateTimePaidUpper = toUtcIsoAtLocalEndOfDay(val);
    }

    get referenceNumber()
    {
        return this.accountSearchCriteria.referenceNumber;
    }

    set referenceNumber(val: string)
    {
        this.accountSearchCriteria.referenceNumber = <string> val;
    }

    get accountNumber()
    {
        return this.accountSearchCriteria.accountNumber;
    }

    set accountNumber(val: string)
    {
        this.accountSearchCriteria.accountNumber = <string> val;
    }

    get customer()
    {
        return this.accountSearchCriteria.customer;
    }

    set customer(val: string)
    {
        this.accountSearchCriteria.customer = <string> val;
    }

    get dateCreatedUpper()
    {
        const date: Date =
            this.accountSearchCriteria.dateTimeCreatedUpper ?
                new Date(this.accountSearchCriteria.dateTimeCreatedUpper) :
                new Date();

        return formatLocalYMD(date);
    }

    get dateCreatedLower()
    {
        return this.accountSearchCriteria.dateTimeCreatedLower ?
            formatLocalYMD(new Date(this.accountSearchCriteria.dateTimeCreatedLower)) :
            null;
    }

    get datePaidUpper()
    {
        const date: Date =
            this.accountSearchCriteria.dateTimePaidUpper ?
                new Date(this.accountSearchCriteria.dateTimePaidUpper) :
                new Date();

        return formatLocalYMD(date);
    }

    get datePaidLower()
    {
        return this.accountSearchCriteria.dateTimePaidLower ?
            formatLocalYMD(new Date(this.accountSearchCriteria.dateTimePaidLower)) :
            null;
    }

    get amountUpper()
    {
        return this.form.get(this._AMOUNT_UPPER_INPUT_ID).value;
    }

    set amountUpper(val)
    {
        this.accountSearchCriteria.amountUpper = val;
    }

    get batchNumber(): number
    {
        return this.accountSearchCriteria.batchNumber;
    }

    set batchNumber(val: number)
    {
        this.accountSearchCriteria.batchNumber = +val;
    }

    get displayStatus(): string
    {
        return this.accountSearchCriteria.displayStatus.valueOf();
    }

    set displayStatus(val: string)
    {
        this.accountSearchCriteria.displayStatus = this.accountStatusUtil.getDisplayStatusFromDescription(val);
    }

    get tabActive(): boolean
    {
        return !!this.activeTab;
    }

    get statusOptions(): DropdownItem[]
    {
        return [
            AccountDisplayStatus.ANY,
            AccountDisplayStatus.PAYMENT_OUTSTANDING,
            AccountDisplayStatus.PROCESSING_PAYMENT,
            AccountDisplayStatus.PAYMENT_COMPLETED,
            AccountDisplayStatus.PARTIAL_PAYMENT_RECEIVED,
            AccountDisplayStatus.PAYMENT_READY_FOR_SETTLEMENT,
            AccountDisplayStatus.SETTLEMENT_PROCESSED,
            AccountDisplayStatus.PAYMENT_CANCELLED,
            AccountDisplayStatus.PAYMENT_EXPIRED,
            AccountDisplayStatus.CANCELLED_DUE_TO_PRICING_PACKAGE_UPDATE
        ].map(status => ({label: status.toString(), value: status}));
    }

    ngOnInit(): void
    {
        this.batchNumber = this.activatedRoute.snapshot.queryParams['batchNumber'] || 0;
        const isBatch = `${this.batchNumber ? 'batch' : 'individual'}`;
        const batchNumber = this.batchNumber != 0 ? this.batchNumber : '';
        this.form = this.formBuilder.group({
            dateTimeCreatedLower: [{value: this.dateCreatedLower}],
            dateTimeCreatedUpper: [{value: this.dateCreatedUpper}],
            accountNumber: [''],
            referenceNumber: [''],
            customer: [''],
            amountLower: [''],
            amountUpper: [''],
            displayStatus: [AccountDisplayStatus.ANY],
            batchNumber: [batchNumber],
            batchSearch: [isBatch],
            dateTimePaidLower: [{value: this.datePaidLower}],
            dateTimePaidUpper: [{value: this.datePaidUpper}],
            ecommerceStoreIds: [this.getSelectedEcommerceStoreIdValue()]
        });
        this.ngxLoader.start();

        if (!this.showBatchFilter && this.activeTab === this.BATCH_FILTER_ID)
        {
            this.activeTab = this.DATE_CREATED_FILTER_ID;
        }

        if (this.batchNumber)
        {
            this.accountSearchCriteria = {
                ...this.accountSearchCriteria,
                individualAccountsOnly: false,
                batchNumber: +this.batchNumber
            };
            this.displayBatchNumber.emit(true);
        }

        this.initializeSearchPage();
    }

    ngOnDestroy(): void
    {
        clearInterval(this.interval);
    }

    setSearchCriteria(event: any)
    {
        const inputId = event.target.id;
        const val = event.target.value;
        this.setAccountSearchCriteria(inputId, val);
    }

    getSearchCriteria(): any
    {
        return this.accountSearchCriteria;
    }

    refresh()
    {
        this.searchDataService.searchableCriteria = this.accountSearchCriteria;
        this.displayBatchNumber.emit(!this.accountSearchCriteria.individualAccountsOnly);
        this.saveCurrentFilters();
        this.searchDataService.refreshSearch({
            ...this.accountSearchCriteria,
            amountUpper: Helper.convertToMinorDenomination(this.accountSearchCriteria.amountUpper),
            amountLower: Helper.convertToMinorDenomination(this.accountSearchCriteria.amountLower)
        });
    }

    setAccountSearchCriteria(inputId: string, val: any)
    {
        switch (inputId)
        {
            case this._AMOUNT_UPPER_INPUT_ID:
                this.accountSearchCriteria.amountUpper = val;
                break;
            case this._AMOUNT_LOWER_INPUT_ID:
                this.accountSearchCriteria.amountLower = val;
                break;
            case this._DATE_TIME_CREATED_LOWER_INPUT_ID:
                this.accountSearchCriteria.dateTimeCreatedLower = toUtcIsoAtLocalStartOfDay(val);
                break;
            case this._DATE_TIME_CREATED_UPPER_INPUT_ID:
                this.accountSearchCriteria.dateTimeCreatedUpper = toUtcIsoAtLocalEndOfDay(val);
                break;
            case this._REFERENCE_NUMBER_INPUT_ID:
                this.accountSearchCriteria.referenceNumber = <string> val;
                break;
            case this._ACCOUNT_NUMBER_INPUT_ID:
                this.accountSearchCriteria.accountNumber = <string> val;
                break;
            case this._CUSTOMER_INPUT_ID:
                this.accountSearchCriteria.customer = <string> val;
                break;
            case this._INDIVIDUAL_ACCOUNTS_INPUT_ID:
                this.accountSearchCriteria.individualAccountsOnly = <boolean> true;
                this.accountSearchCriteria.batchNumber = null;
                this.form.get(this._BATCH_ACCOUNTS_TEXT_INPUT_ID).reset();
                break;
            case this._ALL_ACCOUNTS_INPUT_ID:
                this.accountSearchCriteria.individualAccountsOnly = <boolean> false;
                this.accountSearchCriteria.batchNumber = null;
                this.form.get(this._BATCH_ACCOUNTS_TEXT_INPUT_ID).reset();
                break;
            case this._BATCH_ACCOUNTS_RADIO_INPUT_ID:
                this.accountSearchCriteria.individualAccountsOnly = <boolean> false;
                this.accountSearchCriteria.batchNumber = null;
                this.form.get(this._BATCH_ACCOUNTS_TEXT_INPUT_ID).reset();
                break;
            case this._BATCH_ACCOUNTS_TEXT_INPUT_ID:
                this.accountSearchCriteria.batchNumber = <number> val;
                break;
            case this._DATE_TIME_PAID_LOWER_INPUT_ID:
                this.accountSearchCriteria.dateTimePaidLower = toUtcIsoAtLocalStartOfDay(val);
                break;
            case this._DATE_TIME_PAID_UPPER_INPUT_ID:
                this.accountSearchCriteria.dateTimePaidUpper = toUtcIsoAtLocalEndOfDay(val);
                break;
            case this._ECOMMERCE_STORE_IDS_INPUT_ID:
                this.setEcommerceStoreIds(val);
                break;
        }
    }

    setEcommerceStoreIds(value: any)
    {
        let selectedId: number | null = null;
        if (Array.isArray(value))
        {
            const parsed = Number(value[0]);
            selectedId = Number.isNaN(parsed) ? null : parsed;
        }
        else if (value instanceof Event)
        {
            const target = value.target as HTMLSelectElement;
            const parsed = Number(target?.value);
            selectedId = Number.isNaN(parsed) ? null : parsed;
        }
        else if (value !== '' && value != null)
        {
            const parsed = Number(value);
            selectedId = Number.isNaN(parsed) ? null : parsed;
        }

        // The dropdown value is the BusinessECommerceStore (link-row) id, so it must populate businessECommerceStoreIds
        // — the engine filters that against the link-row id. ecommerceStoreIds is the legacy store-TYPE filter.
        this.accountSearchCriteria.businessECommerceStoreIds = selectedId != null ? [selectedId] : null;
        const controlValue = selectedId != null ? String(selectedId) : '';
        this.form.get(this._ECOMMERCE_STORE_IDS_INPUT_ID)?.setValue(controlValue);
    }

    setStatus(displayStatus: string)
    {
        this.accountSearchCriteria.status = this.accountStatusUtil.getAccountStateFromDescription(
            displayStatus);
        this.accountSearchCriteria.displayStatus = this.accountStatusUtil.getDisplayStatusFromDescription(
            displayStatus);
        this.form.get('displayStatus').setValue(displayStatus);
    }

    getTabContent(event: any)
    {
        this.activeTab = event.target.id.toString();
        this.saveCurrentFilters();
    }

    onCsvPeriodChange(event: Event): void
    {
        this.csvPeriodChange.emit((event.target as HTMLSelectElement).value);
    }

    clear(activeTab: string)
    {
        switch (activeTab)
        {
            case this.DATE_CREATED_FILTER_ID:
                this.dateTimeCreatedUpper = null;
                this.dateTimeCreatedLower = null;
                this.clearDateInputField(this.form, 'dateTimeCreatedLower');
                this.clearDateInputField(this.form, 'dateTimeCreatedUpper');
                break;
            case this.REFERENCE_FILTER_ID:
                this.referenceNumber = null;
                this.form.get('referenceNumber').reset();
                break;
            case this.NAME_FILTER_ID:
                this.customer = null;
                this.form.get('customer').reset();
                break;
            case this.AMOUNT_FILTER_ID:
                this.amountUpper = null;
                this.amountLower = null;
                this.form.get('amountLower').reset();
                this.form.get('amountUpper').reset();
                break;
            case this.ACCOUNT_STATUS_FILTER_ID:
                this.accountSearchCriteria.status = this.accountStatusUtil.getAccountStateFromDescription(
                    AccountDisplayStatus.ANY);
                this.accountSearchCriteria.displayStatus = this.accountStatusUtil.getDisplayStatusFromDescription(
                    AccountDisplayStatus.ANY);
                this.form.get('displayStatus').reset(AccountDisplayStatus.ANY);
                break;
            case this.ACCOUNT_NUMBER_FILTER_ID:
                this.accountNumber = null;
                this.form.get('accountNumber').reset();
                break;
            case this.BATCH_FILTER_ID:
                this.accountSearchCriteria.individualAccountsOnly = false;
                this.batchNumber = null;
                this.form.get(this._BATCH_ACCOUNTS_TEXT_INPUT_ID).reset();
                this.form.get(this._INDIVIDUAL_ACCOUNTS_INPUT_ID).reset();
                break;
            case this.DATE_PAID_FILTER_ID:
                this.dateTimePaidUpper = null;
                this.dateTimePaidLower = null;
                this.clearDateInputField(this.form, 'dateTimePaidLower');
                this.clearDateInputField(this.form, 'dateTimePaidUpper');
                break;
            case this.ECOMMERCE_STORE_FILTER_ID:
                this.accountSearchCriteria.businessECommerceStoreIds = null;
                this.form.get(this._ECOMMERCE_STORE_IDS_INPUT_ID).reset('');
                break;
        }
        this.search();
    }

    clearAll()
    {
        this.accountSearchCriteria = new AccountSearchCriteriaImpl();
        this.form.reset(this.accountSearchCriteria);
        this.search();
    }

    disableClearAll()
    {
        return !this.form.dirty;
    }

    disableClear(activeTab: string): boolean
    {
        switch (activeTab)
        {
            case this.DATE_CREATED_FILTER_ID:
                return !(this.form.get(this._DATE_TIME_CREATED_LOWER_INPUT_ID).dirty || this.form.get(this._DATE_TIME_CREATED_UPPER_INPUT_ID).dirty);
            case this.REFERENCE_FILTER_ID:
                return !this.form.get(this._REFERENCE_NUMBER_INPUT_ID).dirty;
            case this.NAME_FILTER_ID:
                return !this.form.get(this._CUSTOMER_INPUT_ID).dirty;
            case this.AMOUNT_FILTER_ID:
                return !(this.form.get(this._AMOUNT_LOWER_INPUT_ID).dirty || this.form.get(this._AMOUNT_UPPER_INPUT_ID).dirty);
            case this.ACCOUNT_STATUS_FILTER_ID:
                return !this.form.get(this._DISPLAY_STATUS_INPUT_ID).dirty;
            case this.ACCOUNT_NUMBER_FILTER_ID:
                return !this.form.get(this._ACCOUNT_NUMBER_INPUT_ID).dirty;
            case this.BATCH_FILTER_ID:
                return !this.form.get(this._BATCH_ACCOUNTS_TEXT_INPUT_ID).dirty;
            case this.DATE_PAID_FILTER_ID:
                return !(this.form.get(this._DATE_TIME_PAID_LOWER_INPUT_ID).dirty || this.form.get(this._DATE_TIME_PAID_UPPER_INPUT_ID).dirty);
            case this.ECOMMERCE_STORE_FILTER_ID:
                return !this.form.get(this._ECOMMERCE_STORE_IDS_INPUT_ID).dirty;
            default:
                return true;
        }
    }

    setFilters(): void
    {
        this.applyFilters(
            this.form,
            (criteria: AccountSearchCriteria) =>
            {
                this.accountSearchCriteria = {
                    ...new AccountSearchCriteriaImpl(),
                    ...criteria
                };
            },
            (criteria: AccountSearchCriteria) =>
            {
                this.updateBatchNumberAndSearch(criteria);
                this.form.patchValue({
                    ecommerceStoreIds: this.getSelectedEcommerceStoreIdValue(criteria)
                });
            }
        );
    }

    private getSelectedEcommerceStoreIdValue(criteria: AccountSearchCriteria = this.accountSearchCriteria): string
    {
        const selectedId =
            criteria?.businessECommerceStoreIds?.length ? criteria.businessECommerceStoreIds[0] : null;
        return selectedId != null ? String(selectedId) : '';
    }

    updateBatchNumberAndSearch(criteria: AccountSearchCriteria): void
    {
        this.form.patchValue({
            batchNumber: this.batchNumber || '',
            batchSearch: this.batchNumber ? 'batch'
                : criteria?.individualAccountsOnly ? 'individual'
                    : 'all'
        });
    }
}